import {AfterViewInit, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {ActionsService} from "../../actions/actions.service";
import {tap} from "rxjs/operators";
import {ActionModel} from "../../actions/action.model";
import {ActionsViewTabsComponent} from "../../actions/actions-view-tabs/actions-view-tabs.component";
import {ApiDataSource} from "../../utils/api-data-source";
import {AssetModel} from "../../models/asset.model";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {
  AssetsViewCorrectiveActionsFilterComponent
} from "../assets-view-corrective-actions-filter/assets-view-corrective-actions-filter.component";
import {RBACService} from "../../shared/rbac.service";

@Component({
  selector: 'app-assets-view-corrective-actions',
  templateUrl: './assets-view-corrective-actions.component.html',
  styleUrls: ['./assets-view-corrective-actions.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AssetsViewCorrectiveActionsComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetsViewCorrectiveActionsDataSource = new AssetsViewCorrectiveActionsDataSource(this.app, this.api);

  // Built dynamically in ngOnInit(), because the checkbox column only exists to drive bulk delete.
  listColumns: string[] = [];

  // RBAC permission flags, resolved once in ngOnInit().
  //
  // Every write on this tab is a TASKS operation, not an asset one: the row buttons open the tasks
  // view and edit dialogs (ActionsViewTabsComponent) and the delete goes through ActionsService to
  // ActionsController, which gates on the Tasks feature with exact: true and has no asset branch. So
  // these flags follow `tasks`, otherwise an assets-only user gets a 403 dialog from another feature.
  //
  // The list itself is an asset endpoint (AssetsController::getRelatedCorrectiveActions, asset view),
  // which the parent dialog already required to open, so the list is not gated again here.
  canCreateTasks: boolean = false;
  canViewTasks: boolean = false;
  canEditTasks: boolean = false;
  canDeleteTasks: boolean = false;

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  readonly MAX_VISIBLE_ASSIGNED_USERS = 3;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private actionsService: ActionsService,
    public rbac: RBACService
  ) { }

  ngOnInit() {
    this.canCreateTasks = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_CREATE)
    ], true);
    this.canViewTasks = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_VIEW)
    ], true);
    this.canEditTasks = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_EDIT)
    ], true);
    this.canDeleteTasks = this.rbac.userCanAccess([
      RBACService.getPermission(RBACService.RBAC_F.F_TASKS, RBACService.RBAC_A.A_DELETE)
    ], true);

    this.listColumns = [];
    if (this.canDeleteTasks) {
      this.listColumns.push('select');
    }
    this.listColumns.push(
      'id',
      'name',
      'type',
      'priority',
      'due',
      // 'due_UTC',
      'completed',
      'users',
      'date_created',
      // 'date_created_UTC',
      'actions'
    );

    this.dataSource.asset_id = this.asset_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      // Sort UTC columns based on its corresponding date fields.
      if ( sorter['active'] == 'due_UTC' ) {
        this.dataSource.order_by = 'due';
      }

      // Sort UTC columns based on its corresponding date fields.
      if ( sorter['active'] == 'date_created_UTC' ) {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Displays task data.
   *
   * @param {ActionModel} action - The task data to be displayed in the dialog.
   * @return {void}
   */
  onView(action: ActionModel): void {
    if (!this.canViewTasks) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to view tasks.');
      return;
    }

    this.utils.showComponentDialog(ActionsViewTabsComponent, {
      action: action,
      is_edit: false,
      is_system_origin: true,
      hide_asset_link: true
    }, {
      width: '1200px'
    });
  }

  /**
   * Create new or update existing tasks.
   *
   * @param correctiveActionData The corrective action data from the list.
   */
  onEdit(correctiveActionData?: ActionModel): void {
    // This method doubles as Add Task (called with no argument from the template), so it checks the
    // create permission in that case and the edit permission when an existing task was passed.
    if (correctiveActionData) {
      if (!this.canEditTasks) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to edit tasks.');
        return;
      }
    } else {
      if (!this.canCreateTasks) {
        this.rbac.showGenericAccessDeniedMessage('You do not have permission to create tasks.');
        return;
      }
    }

    // Create a new task and apply the data if any.
    let action: ActionModel = new ActionModel();
    if ( correctiveActionData ) {
      action = { ...correctiveActionData } as ActionModel; // Create a copy instead of a reference. This prevents the list data from changing.
    }

    // Assign the task to the asset.
    action.asset_id = this.asset_id;

    // Set the task site id and users if any.
    action.site_id = this.asset.site_id
    action.user_ids = this.asset.users ? this.asset.users.map((user) => user.id) : [];

    if (!action.type) {
      action.type = "Corrective Action";
    }

    // The above fields will be overridden when editing an existing action.
    this.utils.showComponentDialog(ActionsViewTabsComponent, {
      action: action,
      is_edit: true,
      is_system_origin: true
    }, {
      width: '1200px'
    })
      .then((): void => {
        this.dataSource.getData();
      });
  }

  /**
   * Removes selected tasks.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    if (!this.canDeleteTasks) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete tasks.');
      return;
    }

    this.utils.showModal(
      'Remove Tasks',
      `Are you sure you want to remove ${this.dataSource.selection.selected.length} tasks?`,
      () => {
        this.remove(this.dataSource.selection.selected, 'Tasks have been removed');
      }
    );
  }

  /**
   * Removes a selected task.
   *
   * @param {ActionModel} action - The action to be removed.
   * @return {void}
   */
  onRemove(action: ActionModel): void {
    if (!this.canDeleteTasks) {
      this.rbac.showGenericAccessDeniedMessage('You do not have permission to delete tasks.');
      return;
    }

    this.utils.showModal(
      'Remove Task',
      `Are you sure you want to remove "${action.name}"?`,
      () => {
        this.remove([action.id], 'Task has been removed');
      }
    );
  }

  /**
   * Removes tasks with the specified IDs.
   *
   * @param {number[]} ids - The array of task IDs to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: number[], msg: string): void {
    this.actionsService.remove(ids).subscribe(() => {
      this.utils.showToast(msg);
      this.dataSource.selection.clear();
      this.dataSource.getData(false);
    });
  }

  /**
   * Show a component dialog for filtering tasks.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      AssetsViewCorrectiveActionsFilterComponent,
      {
        assigned_users: this.dataSource.user_ids,
        priority: this.dataSource.priority,
        completed: this.dataSource.completed,
        type: this.dataSource.type,
        date_range: this.dataSource.due_date
      },
      {
        width: '1024px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.user_ids = results.assigned_users ?? this.dataSource.user_ids;
          this.dataSource.priority = results.priority ?? this.dataSource.priority;
          this.dataSource.completed = results.completed ?? this.dataSource.completed;
          this.dataSource.type = results.type ?? this.dataSource.type;
          this.dataSource.due_date = results.date_range ?? this.dataSource.due_date;
          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Retrieves a tooltip string containing names of remaining users beyond a maximum visible limit.
   *
   * @param {any[]} users - An array of user objects.
   * @returns {string} A string containing the names of users beyond the maximum visible count.
   */
  getRemainingUsersTooltip(users: any[]): string {
    if (!users || users.length <= this.MAX_VISIBLE_ASSIGNED_USERS) {
      return '';
    }

    return users
      .slice(this.MAX_VISIBLE_ASSIGNED_USERS)
      .map(user => user.contact_person)
      .join(', ');
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class AssetsViewCorrectiveActionsDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  asset_id: number;
  user_ids = [];
  type: string = '';
  priority: string = '';
  completed: number = 0;
  due_date: Date[] = [];

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/corrective-actions`, resetOffset, {
      user_ids: this.user_ids.join(','),
      type: this.type ?? '',
      priority: this.priority ?? '',
      completed: this.completed ?? '0',
      due_date_range: this.due_date && this.due_date.length > 1 ? [
        this.due_date[0].getTime() / 1000,
        this.due_date[1].getTime() / 1000
      ].join(',') : ''
    });
  }
}
